import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import * as ServerListActions from 'actions/ServerList';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import MultiSelectField from 'containers/components/Fields/MultiSelect';
import { getValuesFromList } from 'utils/fm/fields';
import { getLiteral } from 'utils/getLiteral';

const formatSemicolonIds = (ids) => {
    if (!ids || ids.length === 0) return null;
    const normalizedIds = ids.map((id) =>
        String(typeof id === 'object' ? (id.value ?? id.id) : id),
    );
    return `;${normalizedIds.join(';')};`;
};

const RatesEnvironmentsField = memo(
    ({ changeField, data, errors, field, firstErrorField, getError }) => {
        const dispatch = useDispatch();
        const readOnly = !!field.readOnly;
        const error = getError ? getError(errors?.[field.id]) : errors?.[field.id];

        const selectedValue = useMemo(() => {
            const values = getValuesFromList({
                list: 'tblSucursales',
                values: data?.strIdEnvironment,
            });
            return values.length > 0 ? values : undefined;
        }, [data?.strIdEnvironment]);

        const loadOptions = useCallback(() => {
            return dispatch(ServerListActions.getList('tblSucursales')).then((options) => {
                return (options || []).map((item) => ({
                    value: String(item.value ?? item['-id']),
                    label: item.descripcion || item.label || item.strname || '',
                }));
            });
        }, [dispatch]);

        const handleChange = useCallback(
            (selected) => {
                if (!selected || selected.length === 0) {
                    changeField(field.id)(null);
                    return;
                }
                const ids = selected.map((item) =>
                    String(typeof item === 'object' ? (item.value ?? item.id) : item),
                );
                changeField(field.id)(formatSemicolonIds(ids));
            },
            [changeField, field.id],
        );

        return (
            <div
                className={classnames('fm-crud-field', 'fm-rates-environments-field', {
                    [`fm-crud-field__${field.id}`]: !!field.id,
                })}
            >
                <FieldWrapper
                    error={error || null}
                    isFullWidth
                    label={field.label}
                    labelMode={field.labelMode || 'vertical'}
                    mandatory={field.mandatory}
                >
                    <MultiSelectField
                        autoFocus={firstErrorField}
                        fieldId={field.id}
                        hint={getLiteral('label_selectone')}
                        isClearable={!readOnly}
                        labelPopover={false}
                        loadOptions={loadOptions}
                        onChange={handleChange}
                        readOnly={readOnly}
                        value={selectedValue}
                    />
                </FieldWrapper>
            </div>
        );
    },
);

RatesEnvironmentsField.displayName = 'RatesEnvironmentsField';

RatesEnvironmentsField.propTypes = {
    changeField: PropTypes.func.isRequired,
    data: PropTypes.object,
    errors: PropTypes.object,
    field: PropTypes.object.isRequired,
    firstErrorField: PropTypes.bool,
    getError: PropTypes.func,
};

export default RatesEnvironmentsField;
